// FormMainPanel.js
import React, {
  useMemo,
  useState,
  useEffect,
  useRef,
  useCallback,
} from "react";
import "./FormMainPanel.css";
import JSONEditorSOPanel from "./JSONEditorSOPanel";
import FormOptionsDrawer from "./FormOptionsDrawer";
import ControlContentEditor from "./ControlContentEditor";

export default function FormMainPanel({
  selection,
  lang,
  pages = [],
  dictionaryResolvers,
  onChangePhrase,
  onChangeAttributes,
  onToggleRequired,
  onSetAttributes,
  editorMode,
  onEditorModeChange,
  viewfieldsInstance,
  languages = ["en"],
  onSetOptions,
  controlTypeOptions = [],
  onChangeControlType,
  superAdmin = false,
  formJsonText = "",
  formJsonError = "",
  onFormJsonChange,
  // future hooks for full-object updates (not used yet, but wired from FormEditor)
  onFormUpdate,
  onAssetUpdate,
  // export inline/asset options to an asset (FormEditor-level helper)
  // (pageIndex, controlIndex, assetKey, assetContent, meta) => void
  onExportOptionsToAsset,
}) {
  // --- Resolve currently selected control (for breadcrumb + type/required chip) ---

  const resolved = useMemo(() => {
    if (selection?.kind !== "control") {
      return { page: null, control: null, pageIndex: -1, controlIndex: -1 };
    }
    const page = pages?.[selection.pageIndex] || null;
    const control = page?.controls?.[selection.controlIndex] || null;
    return {
      page,
      control,
      pageIndex: selection.pageIndex,
      controlIndex: selection.controlIndex,
    };
  }, [selection, pages]);

  const ctrl = resolved.control;

  // --- Editor "active tool" state (content / attributes / json) ---

  const isControlled =
    typeof editorMode === "string" &&
    typeof onEditorModeChange === "function";

  const normaliseMode = (m) => {
    if (m === "attributes" || m === "json") return m;
    return "content";
  };

  const [localMode, setLocalMode] = useState("content");
  const mode = isControlled ? normaliseMode(editorMode) : localMode;

  const setMode = (m) => {
    const next = normaliseMode(m);
    if (isControlled) {
      onEditorModeChange(next);
    } else {
      setLocalMode(next);
    }
  };

  // --- Type picker state for the selected control (top-right chip) ---

  const [showTypePicker, setShowTypePicker] = useState(false);
  const typeBtnRef = useRef(null);
  const typePickerRef = useRef(null);

  useEffect(() => {
    if (!showTypePicker) return;

    const onDocClick = (e) => {
      if (
        typePickerRef.current &&
        !typePickerRef.current.contains(e.target) &&
        typeBtnRef.current &&
        !typeBtnRef.current.contains(e.target)
      ) {
        setShowTypePicker(false);
      }
    };
    const onKey = (e) => {
      if (e.key === "Escape") setShowTypePicker(false);
    };

    document.addEventListener("mousedown", onDocClick);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDocClick);
      document.removeEventListener("keydown", onKey);
    };
  }, [showTypePicker]);

  const pickType = (t) => {
    onChangeControlType?.(t);
    setShowTypePicker(false);
  };

  // --- Options / Attributes / JSON drawers ---

  const [isOptionsOpen, setIsOptionsOpen] = useState(false);
  const [optionsTarget, setOptionsTarget] = useState(null);

  const [isAttributesOpen, setIsAttributesOpen] = useState(false);
  const [attributesTarget, setAttributesTarget] = useState(null);

  const [isJsonOpen, setIsJsonOpen] = useState(false);
  const [jsonTarget, setJsonTarget] = useState(null);

  // --- Dictionary helpers (shared, no phrase map duplication) ---

  const getPhraseSync = dictionaryResolvers?.getPhraseSync;

  const phraseHasKey = useCallback(
    (l, key) => {
      if (!key || typeof getPhraseSync !== "function") return false;
      const v = getPhraseSync(l, key);
      // "key returned unchanged" means "no phrase"
      return typeof v === "string" && v !== key;
    },
    [getPhraseSync]
  );

  const phraseGet = useCallback(
    (l, key) => {
      if (!key || typeof getPhraseSync !== "function") return undefined;
      const v = getPhraseSync(l, key);
      return typeof v === "string" && v !== key ? v : undefined;
    },
    [getPhraseSync]
  );

  // --- Which controls are in CONTENT view? (single vs multi) ---

  const controlsForContent = useMemo(() => {
    if (!Array.isArray(pages)) return [];

    if (selection?.kind === "control") {
      const page = pages[selection.pageIndex] || null;
      const c = page?.controls?.[selection.controlIndex] || null;
      return c
        ? [
            {
              control: c,
              pageIndex: selection.pageIndex,
              controlIndex: selection.controlIndex,
            },
          ]
        : [];
    }

    if (selection?.kind === "page") {
      const page = pages[selection.pageIndex] || null;
      const list = Array.isArray(page?.controls) ? page.controls : [];
      return list.map((c, idx) => ({
        control: c,
        pageIndex: selection.pageIndex,
        controlIndex: idx,
      }));
    }

    return [];
  }, [selection, pages]);

  const isSingleContent = controlsForContent.length === 1;

  // --- Header dictionary key chip (single-control mode only) ---

  const { headerEditKey, headerIsMissing } = useMemo(() => {
    if (!isSingleContent) {
      return { headerEditKey: "", headerIsMissing: false };
    }
    const { control } = controlsForContent[0];
    if (!control) return { headerEditKey: "", headerIsMissing: false };

    const keyCandidates = [
      control.label || null,
      control.content || null,
      control.key || null,
    ].filter(Boolean);

    if (keyCandidates.length === 0) {
      return { headerEditKey: "", headerIsMissing: false };
    }

    const ek =
      keyCandidates.find((k) => phraseHasKey(lang, k)) ||
      keyCandidates[keyCandidates.length - 1];

    const isMissing = !!ek && !phraseHasKey(lang, ek);

    return { headerEditKey: ek, headerIsMissing: isMissing };
  }, [controlsForContent, phraseHasKey, lang, isSingleContent]);

  // --- Helpers for types / options support ---

  const typeSupportsOptions = (t) => {
    const T = String(t || "").toUpperCase();
    return ["SELECT", "RADIO", "RATING", "CHECKBOX"].includes(T);
  };

  // --- Breadcrumb text ---

  const pageTitle =
    (resolved.page?.title &&
      (phraseGet(lang, resolved.page.title) ?? resolved.page.title)) ||
    (resolved.pageIndex >= 0 ? `Page ${resolved.pageIndex + 1}` : "");

  // --- Required toggles (top-level & per-row) ---

  const handleRequiredChange = (next) => {
    if (!ctrl) return;
    if (typeof onToggleRequired === "function") {
      onToggleRequired(resolved.pageIndex, resolved.controlIndex, !!next);
    } else if (typeof onChangeAttributes === "function") {
      onChangeAttributes(resolved.pageIndex, resolved.controlIndex, {
        required: !!next,
      });
    }
  };

  const handleRowRequiredChange = (pageIndex, controlIndex, next) => {
    if (typeof onToggleRequired === "function") {
      onToggleRequired(pageIndex, controlIndex, !!next);
    } else if (typeof onChangeAttributes === "function") {
      onChangeAttributes(pageIndex, controlIndex, { required: !!next });
    }
  };

  // --- Toolbar handlers from per-control CAJO icons ---

  const handleControlAttributesClick = (pageIndex, controlIndex) => {
    const page = pages?.[pageIndex];
    const control = page?.controls?.[controlIndex];
    if (!control) return;

    setAttributesTarget({ pageIndex, controlIndex });
    setIsAttributesOpen(true);
    setMode("attributes");
  };

  const handleControlOptionsClick = (pageIndex, controlIndex) => {
    const page = pages?.[pageIndex];
    const control = page?.controls?.[controlIndex];
    if (!control) return;
    if (!typeSupportsOptions(control.type)) return;

    setOptionsTarget({ pageIndex, controlIndex });
    setIsOptionsOpen(true);
    setMode("content"); // content stays main; options is side drawer
  };

  const handleControlJsonClick = (pageIndex, controlIndex, controlObj) => {
    const safeControl =
      controlObj || pages?.[pageIndex]?.controls?.[controlIndex];
    if (!safeControl) return;

    setJsonTarget({ pageIndex, controlIndex });
    setIsJsonOpen(true);
    setMode("json");
  };

  // --- Drawer targets derived from indices ---

  const optionsControl = useMemo(() => {
    if (!optionsTarget) return null;
    const page = pages?.[optionsTarget.pageIndex];
    return page?.controls?.[optionsTarget.controlIndex] || null;
  }, [optionsTarget, pages]);

  const attributesControl = useMemo(() => {
    if (!attributesTarget) return null;
    const page = pages?.[attributesTarget.pageIndex];
    return page?.controls?.[attributesTarget.controlIndex] || null;
  }, [attributesTarget, pages]);

  const jsonControl = useMemo(() => {
    if (!jsonTarget) return null;
    const page = pages?.[jsonTarget.pageIndex];
    return page?.controls?.[jsonTarget.controlIndex] || null;
  }, [jsonTarget, pages]);

  // --- Top row: breadcrumb + type + required ---

  return (
    <div className="fe-mainpanel">
      <div className="fe-toprow">
        <div className="fe-breadcrumb">
          {ctrl ? (
            <>
              Form / {pageTitle} / <strong>{ctrl.key || "Control"}</strong>
            </>
          ) : selection?.kind === "page" ? (
            <>
              Form /{" "}
              <strong>
                {pages?.[selection.pageIndex]?.title ||
                  `Page ${selection.pageIndex + 1}`}
              </strong>
            </>
          ) : (
            "Form"
          )}
        </div>

        {resolved.control && (
          <div className="fe-quick">
            {resolved.control.type ? (
              <button
                ref={typeBtnRef}
                type="button"
                className="fe-chip fe-chip--clickable"
                title="Change control type"
                aria-haspopup="listbox"
                aria-expanded={showTypePicker}
                onClick={() => setShowTypePicker((s) => !s)}
              >
                {String(resolved.control.type).toUpperCase()}
              </button>
            ) : null}

            {showTypePicker &&
              Array.isArray(controlTypeOptions) &&
              controlTypeOptions.length > 0 && (
                <div
                  ref={typePickerRef}
                  className="fe-typepicker"
                  role="listbox"
                  aria-label="Control type"
                >
                  {controlTypeOptions.map((t) => {
                    const active =
                      String(resolved.control?.type || "").toUpperCase() ===
                      String(t).toUpperCase();
                    return (
                      <button
                        key={t}
                        type="button"
                        role="option"
                        aria-selected={active}
                        className={`fe-typeopt ${active ? "is-active" : ""}`}
                        onClick={() => pickType(t)}
                      >
                        {t}
                      </button>
                    );
                  })}
                </div>
              )}

            <label className="fe-switch" title="Required">
              <input
                type="checkbox"
                checked={!!resolved.control?.required}
                onChange={(e) => handleRequiredChange(e.target.checked)}
              />
              Required
            </label>
          </div>
        )}
      </div>

      {/* Header (Attributes / JSON / Options) – only for single-control mode */}
      {isSingleContent && (
        <div className="fe-headerrow">
          {/* Left side: label + key chip */}
          <div className="fe-header-left">
            <label className="fe-label">
              Content ({lang.toUpperCase()})
              {headerEditKey && (
                <span
                  className={
                    "fe-dictkey " +
                    (headerIsMissing
                      ? "fe-dictkey--missing"
                      : "fe-dictkey--ok")
                  }
                  title={
                    headerIsMissing
                      ? `Key "${headerEditKey}" is missing in the ${lang.toUpperCase()} dictionary`
                      : `Dictionary key for this content`
                  }
                >
                  <span className="fe-dictkey-prefix">Key:</span>
                  <code className="fe-dictkey-code">{headerEditKey}</code>
                </span>
              )}
            </label>
          </div>

          {/* Right side: fixed-width button bar, centred within its area */}
          <div className="fe-header-buttons">
            <div className="fe-segtabs" role="tablist" aria-label="Edit mode">
              <button
                className={`fe-seg ${
                  mode === "attributes" ? "is-active" : ""
                }`}
                type="button"
                role="tab"
                aria-selected={mode === "attributes"}
                onClick={() => {
                  const only = controlsForContent[0];
                  if (!only) return;
                  setAttributesTarget({
                    pageIndex: only.pageIndex,
                    controlIndex: only.controlIndex,
                  });
                  setIsAttributesOpen(true);
                  setMode("attributes");
                }}
                disabled={!ctrl}
              >
                Attributes
              </button>

              <button
                className={`fe-seg ${mode === "json" ? "is-active" : ""}`}
                type="button"
                role="tab"
                aria-selected={mode === "json"}
                onClick={() => {
                  const only = controlsForContent[0];
                  if (!only) return;
                  setJsonTarget({
                    pageIndex: only.pageIndex,
                    controlIndex: only.controlIndex,
                  });
                  setIsJsonOpen(true);
                  setMode("json");
                }}
                disabled={!ctrl}
              >
                JSON
              </button>

              {ctrl && typeSupportsOptions(ctrl.type) && (
                <button
                  className="fe-seg"
                  type="button"
                  role="tab"
                  aria-selected={false}
                  onClick={() => {
                    setOptionsTarget({
                      pageIndex: resolved.pageIndex,
                      controlIndex: resolved.controlIndex,
                    });
                    setIsOptionsOpen(true);
                    setMode("content");
                  }}
                  title="Edit options"
                >
                  Options…
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Main content area: ALWAYS content (dictionary textareas) */}
      <div className="fe-contentwrap">
        {controlsForContent.length > 0 ? (
          <div className="fe-contentlist">
            {controlsForContent.map(({ control, pageIndex, controlIndex }) => (
              <ControlContentEditor
                key={control.key || `p${pageIndex}-c${controlIndex}`}
                control={control}
                lang={lang}
                phraseHasKey={phraseHasKey}
                phraseGet={phraseGet}
                onChangePhrase={onChangePhrase}
                showKeyChip={!isSingleContent}
                isSingle={isSingleContent}
                pageIndex={pageIndex}
                controlIndex={controlIndex}
                hasOptions={typeSupportsOptions(control.type)}
                required={!!control.required}
                onToggleRequired={handleRowRequiredChange}
                onAttributesClick={handleControlAttributesClick}
                onOptionsClick={handleControlOptionsClick}
                onJsonClick={handleControlJsonClick}
              />
            ))}
          </div>
        ) : (
          <div className="fe-empty">
            Select a page or control to edit its content.
          </div>
        )}
      </div>

      {/* Attributes drawer (per-control) */}
      {isAttributesOpen && attributesControl && attributesTarget && (
        <div className="fe-sidepanel fe-sidepanel--attributes">
          <div className="fe-sidepanel-header">
            <span className="fe-sidepanel-title">Attributes</span>
            <button
              type="button"
              className="fe-sidepanel-close"
              onClick={() => {
                setIsAttributesOpen(false);
                setMode("content");
              }}
              aria-label="Close attributes"
            >
              ×
            </button>
          </div>
          <div className="fe-sidepanel-body">
            <JSONEditorSOPanel
              initialObject={attributesControl.attributes || {}}
              onApply={(nextObj) => {
                if (typeof onSetAttributes === "function") {
                  onSetAttributes(
                    nextObj,
                    attributesTarget.pageIndex,
                    attributesTarget.controlIndex
                  );
                } else if (typeof onChangeAttributes === "function") {
                  onChangeAttributes(
                    attributesTarget.pageIndex,
                    attributesTarget.controlIndex,
                    nextObj
                  );
                }
              }}
              onCancel={() => {
                setIsAttributesOpen(false);
                setMode("content");
              }}
              applyLabel="Apply"
              noChangesLabel="No changes"
              unsavedLabel="Unsaved changes"
              errorPrefix="JSON error"
            />
          </div>
        </div>
      )}

      {/* JSON drawer (per-control) */}
      {isJsonOpen && jsonControl && jsonTarget && (
        <div className="fe-sidepanel fe-sidepanel--json">
          <div className="fe-sidepanel-header">
            <span className="fe-sidepanel-title">Control JSON</span>
            <button
              type="button"
              className="fe-sidepanel-close"
              onClick={() => {
                setIsJsonOpen(false);
                setMode("content");
              }}
              aria-label="Close JSON"
            >
              ×
            </button>
          </div>
          <div className="fe-sidepanel-body">
            <JSONEditorSOPanel
              initialObject={jsonControl || {}}
              onApply={() => {
                // TODO: when you decide how control-level JSON should be persisted,
                // wire that here via onFormUpdate or a dedicated helper.
              }}
              onCancel={() => {
                setIsJsonOpen(false);
                setMode("content");
              }}
              applyLabel="Apply"
              noChangesLabel="No changes"
              unsavedLabel="Unsaved changes"
              errorPrefix="JSON error"
            />
          </div>
        </div>
      )}

      {/* Options drawer (slide-out sidepanel) */}
      {isOptionsOpen && optionsControl && optionsTarget && (
        <div className="fe-sidepanel fe-sidepanel--options">
          <div className="fe-sidepanel-header">
            <span className="fe-sidepanel-title">
              Options – {optionsControl.key || optionsControl.name || "Control"}
            </span>
            <button
              type="button"
              className="fe-sidepanel-close"
              onClick={() => {
                setIsOptionsOpen(false);
              }}
              aria-label="Close options"
            >
              ×
            </button>
          </div>
          <div className="fe-sidepanel-body">
            <FormOptionsDrawer
              onClose={() => setIsOptionsOpen(false)}
              control={optionsControl}
              viewfieldsInstance={viewfieldsInstance}
              dictionaryResolvers={dictionaryResolvers}
              languages={languages}
              lang={lang}
              onApplyInline={(nextInline) => {
                if (!optionsTarget) return;
                if (typeof onSetOptions === "function") {
                  onSetOptions(
                    nextInline,
                    optionsTarget.pageIndex,
                    optionsTarget.controlIndex
                  );
                }
              }}
              onChangePhrase={onChangePhrase}
              onExportToAsset={(assetKey, content) => {
                if (!optionsTarget) return;

                console.log("[FormMainPanel] onExportToAsset", {
                  assetKey,
                  optionsCount: Array.isArray(content) ? content.length : 0,
                  pageIndex: optionsTarget.pageIndex,
                  controlIndex: optionsTarget.controlIndex,
                });

                if (typeof onExportOptionsToAsset === "function") {
                  const meta = {
                    // Single source of truth:
                    // - this drawer exports OPTIONS assets
                    // - language is the current editor language
                    // - name/description derived from assetKey
                    type: "OPTIONS",
                    language: lang,
                    name: assetKey,
                    description: `Asset ${assetKey}`,
                  };

                  onExportOptionsToAsset(
                    optionsTarget.pageIndex,
                    optionsTarget.controlIndex,
                    assetKey,
                    content,
                    meta
                  );
                }
              }}
            />
          </div>
        </div>
      )}
    </div>
  );
}
